<script setup lang="ts">
import * as d3 from "d3";
import moment from "moment";
import { useRouter } from "vue-router";
import { any, chain, sortBy } from "underscore";
import { computed, ref, useTemplateRef, watch } from "vue";

import ConfirmPopover from "@/components/ConfirmPopover.vue";
import TagTooltip from "@/components/Tooltip/TagTooltip.vue";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";
import EvidenceCountTooltip from "@/components/Tooltip/EvidenceCountTooltip.vue";

import { Metadata } from "@/models/Metadata";

import { useIoaStore } from "@/stores/ioa-store";
import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { useFiltersStore } from "@/stores/filters-store";
import { useMetadataStore } from "@/stores/metadata-store";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { getGracePeriod, ioaOverlapsAgreementPeriod, toMoment, $can } from "@/services/UtilitiesService";

import type { TagBase } from "@/models/Tag";
import type { IconConfig } from "@/models/BrandViewerConfig";
import type AgreementPeriod from "@/models/AgreementPeriod";
import type InstanceOfAdvice from "@/models/InstanceOfAdvice";
import type EvidenceCount from "@/models/RecordEvidenceCount";

enum TimelineState {
  IDLE,
  ZOOMED,
  ZOOMED_RIGHT_END,
  ZOOMED_LEFT_END,
}

const stringToDateCache: Record<string, Date> = {};
const END_OF_DAY = { hours: 23, minutes: 59, seconds: 59 };

const MOVE_ICON_OFFSET_X = 10;
const MOVE_ICON_OFFSET_Y = -2;
const MOVE_ICON_WIDTH = 24;
const MOVE_ICON_HEIGHT = 24;
const MOVE_ICON_OPACITY = 0.4;
const RECORD_CLUSTER_ICON_WIDTH = 18;
const RECORD_CLUSTER_ICON_HEIGHT = 15;
const INSTANCE_LABEL_HEIGHT = 16;
const EVIDENCE_COUNT_BADGE_WIDTH = 20;
const EVIDENCE_COUNT_BADGE_HEIGHT = 12;
const AXIS_STROKE_COLOR = "grey";
const AXIS_BAND_HEIGHT = 20;
const AGREEMENT_PERIOD_FILL = "#cdcdcd";
const AXIS_LABEL_OFFSET_X = 7;
const AXIS_LABEL_OFFSET_Y = 14;
// Nudges the start edge of a tick/period rect left by 1px so its border lines up with the axis tick
const EDGE_ALIGNMENT_OFFSET_X = -1;

const DIMENSIONS_PADDING_TOP = 1;
const DIMENSIONS_PADDING_LEFT = 0;
const DIMENSIONS_PADDING_RIGHT = 0;
const DIMENSIONS_PADDING_BOTTOM = 20;

const GRACE_TRIANGLE_WIDTH = 65;
const GRACE_LABEL_OFFSET_X = -25;
const GRACE_LABEL_OFFSET_Y = 17;

const RECORD_CLUSTER_ICON_SPACING_X = 20;
const RECORD_CLUSTER_ICON_OFFSET_Y = -40;

const INSTANCE_ROW_OFFSET_Y_ASSOCIATED = 20;
const INSTANCE_ROW_OFFSET_Y_UNASSOCIATED = 22;
const INSTANCE_CORNER_RADIUS_ASSOCIATED = 0;
const INSTANCE_CORNER_RADIUS_UNASSOCIATED = 5;

const EVIDENCE_COUNT_BADGE_SPACING_X = 25;
const EVIDENCE_COUNT_BADGE_OFFSET_Y_LOWER = 22;
const EVIDENCE_COUNT_BADGE_OFFSET_Y_UPPER = -18;
const EVIDENCE_COUNT_BADGE_CORNER_RADIUS = 3;

const IOA_START_PADDING = 4;
const IOA_END_PADDING = 8;
const IOA_MIN_WIDTH = 18;

const PAN_STEP = 10;

const $router = useRouter();
const routeParams = useCaseRouteParams();

const zoomRef = useTemplateRef<Element>("zoomRef");
const containerEl = useTemplateRef<Element>("containerEl");

let zoom: ReturnType<typeof d3.zoom> | undefined;

const dimensions = ref({ width: window.innerWidth - 34, height: 70 });
const transform = ref<d3.ZoomTransform | null>(null);

const initialFilter = useFiltersStore().filter.dateRange;
const filteredItems = ref<string[]>([]);
const periodId = ref("");
const lastFilteredFileReference = ref("");

const instanceFilter = computed(() => useFiltersStore().filter.adviceInstanceId);

const timelineState = computed(() => {
  if (transform.value != null && transform.value.k > 1) {
    const [, rangeEnd] = xScale.value.range();

    // 3 is buffer to avoid cursor blinks
    if (rangeEnd * transform.value.k + transform.value.x <= rangeEnd + 3) return TimelineState.ZOOMED_RIGHT_END;
    if (transform.value.x >= -3) return TimelineState.ZOOMED_LEFT_END;
    return TimelineState.ZOOMED;
  }

  return TimelineState.IDLE;
});

const dataset = computed(() => useMetadataStore().requestedFiles.filter((f) => !!toMoment(f.edxIssueDate || "", true)));

const config = computed(() => useBrandStore().viewerConfig.timeline ?? {});

const isGraceDisabled = computed(() => !!config.value.upper?.disableOverlapMonths);

const agreementPeriodConfig = computed((): { [x: string]: { [y: string]: string } } => {
  return (
    config.value.upper?.periodOptions.reduce(
      (l, r) => ({
        ...l,
        [r.name]: r.enumeration.reduce((il, ir) => ({ ...il, [ir.name]: ir.icon }), {}),
      }),
      {},
    ) ?? {}
  );
});

const periodOptions = computed(() => {
  return chain(config.value.upper?.periodOptions ?? [])
    .filter((f) => f.type !== "manual" || any(f.enumeration, (t) => !!t.iconType))
    .map((f) => {
      // Initialize sequence
      const sequence = f.sequence || 0;
      // Sort enumerations by sequence
      const enumeration = sortBy(
        f.enumeration.map((e) => ({ ...e, sequence: e.sequence || 0 })),
        "sequence",
      );
      return { ...f, sequence, enumeration };
    })
    .sortBy((option) => option.sequence)
    .value();
});

const agreementPeriods = computed(() => {
  const FORMAT = "YYYY MM DD";

  return useCaseStore()
    .config.agreementPeriods.filter((ap) => ap.from && ap.to)
    .map(getGracePeriod)
    .sort((a, b) => {
      const aInstance = moment(a.from, FORMAT, true);
      const bInstance = moment(b.from, FORMAT, true);

      if (aInstance.isAfter(bInstance)) {
        return 1;
      }
      return -1;
    });
});

const recordClusters = computed(() => {
  interface Circle extends Metadata {
    r: number;
    startX: number;
    endX: number;
    tags: NonNullable<Metadata["tags"]>;
    autoTags: NonNullable<Metadata["autoTags"]>;
    evidenceCounts: NonNullable<Metadata["evidenceCounts"]>;
    iconConfigs: Array<
      ({ type: "tag"; value: TagBase } | { type: "evidenceCount"; value: EvidenceCount }) & {
        config: IconConfig;
        original: Metadata["original"];
      }
    >;
  }

  const visibleDataset = dataset.value
    .map((data) => {
      return {
        ...data,
        x: xScale.value(xAccessor(data)),
      };
    })
    .filter((c) => c.x! >= 0 && c.x! <= dimensions.value.width)
    .sort((f1, f2) => f1.x! - f2.x!);

  // Calculate cluster size thresholds based on available records
  const quantile = d3
    .scaleQuantile()
    .domain([0, Math.max(visibleDataset.length, 4)])
    .range([8, 12, 16, 32]);

  const iconsConfig = config.value.upper?.icons ?? {};
  const mappedIconConfig = Object.entries(iconsConfig).reduce(
    (
      acc: Partial<
        Record<
          "autoTag" | "userTag" | "evidenceCounts",
          | { type: "nested"; value: Record<string, Record<string, IconConfig | undefined>> }
          | { type: "flat"; value: Record<string, IconConfig | undefined> }
          | undefined
        >
      >,
      [_key, val],
    ) => {
      const key = _key as keyof typeof iconsConfig;

      const buildLookupMap = (items: IconConfig[]) => {
        const lookupMap: Record<string, IconConfig> = {};
        for (const item of items) lookupMap[item.value.toLowerCase()] = item;
        return lookupMap;
      };

      acc[key] = Array.isArray(val)
        ? { type: "flat" as const, value: buildLookupMap(val) }
        : {
            type: "nested" as const,
            value: Object.entries(val).reduce(
              (subAcc, [subKey, subVal]) => {
                subAcc[subKey] = buildLookupMap(subVal);
                return subAcc;
              },
              {} as Record<string, ReturnType<typeof buildLookupMap>>,
            ),
          };

      return acc;
    },
    {},
  );

  return visibleDataset.reduce((aggregator: Circle[], curr: Metadata) => {
    const CIRCLE_RADIUS = 4;
    const circle: Circle = {
      ...curr,
      r: CIRCLE_RADIUS,
      startX: curr.x - CIRCLE_RADIUS,
      endX: curr.x + CIRCLE_RADIUS,
      tags: curr.tags ?? [],
      autoTags: curr.autoTags ?? [],
      evidenceCounts: curr.evidenceCounts ?? [],
      iconConfigs: [],
    };

    if (mappedIconConfig.autoTag?.type === "nested") {
      for (const value of circle.autoTags) {
        const iconConfig = mappedIconConfig.autoTag.value[value.tagType]?.[value.name.toLowerCase()];
        if (iconConfig != null)
          circle.iconConfigs.push({ type: "tag", value, config: iconConfig, original: circle.original });
      }
    }

    if (mappedIconConfig.userTag?.type === "nested") {
      for (const value of circle.tags) {
        const iconConfig = mappedIconConfig.userTag.value[value.tagType]?.[value.name.toLowerCase()];
        if (iconConfig != null)
          circle.iconConfigs.push({ type: "tag", value, config: iconConfig, original: circle.original });
      }
    }

    if (mappedIconConfig.evidenceCounts?.type === "flat") {
      for (const value of circle.evidenceCounts) {
        const iconConfig = mappedIconConfig.evidenceCounts.value[value.name.toLowerCase()];
        if (iconConfig != null)
          circle.iconConfigs.push({ type: "evidenceCount", value, config: iconConfig, original: circle.original });
      }
    }

    // See if previous circle can gobble up this one
    const prevCircle = aggregator[aggregator.length - 1];
    if (prevCircle && prevCircle.endX > circle.startX) {
      prevCircle.children = prevCircle.children || [];
      prevCircle.children.push(circle);

      // Calculate circle dimensions again
      prevCircle.r = quantile(prevCircle.children.length as number)!;
      prevCircle.startX = prevCircle.x - prevCircle.r;
      prevCircle.endX = prevCircle.x + prevCircle.r;

      prevCircle.iconConfigs = [...prevCircle.iconConfigs, ...circle.iconConfigs];
      return [...aggregator];
    } else {
      return [...aggregator, circle];
    }
  }, []);
});

const ioas = computed(() => {
  const map = new Map<string, InstanceOfAdvice>();
  const list: Array<InstanceOfAdvice & Record<"width" | "x", number>> = [];

  for (const ioa of useIoaStore().instanceOfAdvices) {
    map.set(ioa.instanceReference, ioa);

    const startX = xScale.value(ioa.startDate!)! - IOA_START_PADDING;
    const endX = xScale.value(ioa.endDate!)! + IOA_END_PADDING;

    if (isNaN(startX) || isNaN(endX)) continue;

    list.push({ ...ioa, x: startX, width: Math.max(endX - startX, IOA_MIN_WIDTH) });
  }

  return { map, list };
});

const ioAsAssociated = computed(() => {
  const result = new Set<string>();
  for (const a of agreementPeriods.value) a.ioas?.forEach((ioa) => result.add(ioa));
  return result;
});

const ioAsToAssociate = computed(() =>
  ioas.value.list.filter(
    (i) => !ioAsAssociated.value.has(i.instanceReference) && ioaOverlapsAgreementPeriod(selectedPeriod.value, i),
  ),
);

// Get all dates from records and agreement periods
// Used to create base x-axis scale for the chart
const dateRange = computed(() => {
  const allDates = [
    ...dataset.value.map(xAccessor),
    ...agreementPeriods.value
      .filter((a) => a.from && a.to)
      .flatMap((a) => [stringToDate(a.graceFrom || a.from), stringToDate(a.graceTo || a.to)]),
  ];
  const [min, max] = d3.extent(allDates);

  // Add 2 months buffer. This provides more room on left and right most corners
  if (min != null && max != null) {
    min.setMonth(min.getMonth() - 2);
    max.setMonth(max.getMonth() + 2);
    return [min, max];
  } else return [];
});

const selectedPeriod = computed<AgreementPeriod>(() => {
  return (
    (periodId.value === "" ? undefined : agreementPeriods.value.find((f) => String(f.id) === String(periodId.value))) ??
    agreementPeriods.value[0] ??
    ({} as AgreementPeriod)
  );
});

const xScale = computed(() => {
  const full = d3.scaleTime().domain(dateRange.value).range([0, dimensions.value.width]);
  return transform.value == null ? full : transform.value.rescaleX(full);
});

const PIXELS_PER_TICK = 300;
const numberOfTicks = computed(() => dimensions.value.width / PIXELS_PER_TICK);
const ticks = computed(() => xScale.value.ticks(numberOfTicks.value));

useFiltersStore().$onAction(({ name }) => {
  if (name === "resetTriggered") filteredItems.value = [];
});

watch([xScale, transform], () => {
  if (transform.value == null || zoom == null) return;

  const [firstDay, secondDay] = xScale.value.ticks();
  if (firstDay == null || secondDay == null) return;

  const daysDifference = (secondDay.getTime() - firstDay.getTime()) / (24 * 60 * 60 * 1000);

  // Restrict Zoom in to only upto 1 day
  zoom.scaleExtent([1, transform.value.k === 1 || daysDifference >= 1 ? Infinity : transform.value.k]);
});

watch(
  [containerEl, zoomRef],
  (_a, _b, onCleanup) => {
    if (containerEl.value == null || zoomRef.value == null) return;

    zoom = d3.zoom().on("zoom", () => void (transform.value = d3.event.transform as d3.ZoomTransform));

    const zoomSelection = d3.select(zoomRef.value).call(zoom).on("dblclick.zoom", null);

    const resizeObserver = new ResizeObserver(([entry], observer) => {
      if (zoom == null) {
        observer.disconnect();
        return;
      }

      dimensions.value = {
        width: entry.contentRect.width - DIMENSIONS_PADDING_LEFT - DIMENSIONS_PADDING_RIGHT,
        height: entry.contentRect.height - DIMENSIONS_PADDING_TOP - DIMENSIONS_PADDING_BOTTOM,
      };

      zoom.extent([
        [0, 0],
        [dimensions.value.width, dimensions.value.height],
      ]);
      zoom.translateExtent([
        [0, 0],
        [dimensions.value.width, dimensions.value.height],
      ]);
      if (transform.value != null) zoom.transform(zoomSelection, transform.value);
    });

    resizeObserver.observe(containerEl.value);
    onCleanup(() => resizeObserver.disconnect());
  },
  { immediate: true },
);

async function onPeriodChanged(): Promise<void> {
  if (!selectedPeriod.value) return;

  await useCaseStore().updateAgreementPeriod(selectedPeriod.value);
}

async function addIoaToPeriod(event: Event) {
  const selectElement = event.srcElement as HTMLSelectElement;
  if (!selectElement?.value) return;

  await useCaseStore().updateAgreementPeriod({
    ...selectedPeriod.value,
    ioas: [...(selectedPeriod.value.ioas ?? []), selectElement.value],
  });
  selectElement.value = "";
}

async function deleteIoaFromPeriod(ioaReference: string) {
  await useCaseStore().updateAgreementPeriod({
    ...selectedPeriod.value,
    ioas: selectedPeriod.value.ioas?.filter((i: string) => i !== ioaReference),
  });
}

function pan(event: Event, x: number) {
  event.stopPropagation();
  if (zoom == null) return;

  const selection = d3.select(zoomRef.value!);
  zoom.translateBy(selection, x, 0);
}

function scale(k: number) {
  if (zoom == null) return;

  const selection = d3.select(zoomRef.value!);
  zoom.scaleBy(selection, k);
}

function changeDateFilter(cluster: Metadata) {
  // Reset the other IoA filters
  useFiltersStore().changeAdviceInstanceId({ instanceReference: "" });

  // Reset filters if filters are applied already
  if (filteredItems.value.length && filteredItems.value.includes(cluster.fileReference)) {
    useFiltersStore().changeDateRange(initialFilter);
    filteredItems.value = [];
    lastFilteredFileReference.value = "";
  } else {
    const [from, to] = d3
      .extent([cluster, ...(cluster.children || [cluster])], xAccessor)
      .map((d: Date | undefined) => moment(d).format("YYYY MM DD"));

    // Add all items in the filter so as to indicate the
    const children = cluster.children ? cluster.children.map((c: Metadata) => c.fileReference) : [];
    filteredItems.value = [cluster.fileReference, ...children];
    useFiltersStore().changeDateRange({ from, to, includeBlanks: false });
  }
}

function applyInstanceFilter(instance: InstanceOfAdvice) {
  // Reset the other date range filters
  useFiltersStore().changeDateRange(initialFilter);
  filteredItems.value = [];

  if (!instanceFilter.value || instanceFilter.value !== instance.instanceReference) {
    useFiltersStore().changeAdviceInstanceId({
      instanceReference: instance.instanceReference,
    });
  } else {
    useFiltersStore().changeAdviceInstanceId({ instanceReference: "" });
  }
}

function xAccessor(metadata: Metadata): Date {
  return stringToDate(metadata.edxIssueDate!);
}

function instanceBg(instance: InstanceOfAdvice) {
  const values = config.value.instances?.instanceStyle.values ?? [];
  const instanceConfig = values.find((v) => instance.tags.findIndex((ia) => ia.name === v.value) > -1);
  return instanceConfig ? instanceConfig.background : config.value.instances?.instanceStyle.defaultBackground;
}

function instanceColor(instance: InstanceOfAdvice) {
  const values = config.value.instances?.instanceStyle.values ?? [];
  const instanceConfig = values.find((v) => instance.tags.findIndex((ia) => ia.name === v.value) > -1);
  return instanceConfig ? instanceConfig.color : config.value.instances?.instanceStyle.defaultColour;
}

function evidenceCountConfig(tag: string) {
  const c = config.value.instances?.instanceIcons.find((v) => v.recordCount.toLowerCase() === tag.toLowerCase());

  return (
    c || {
      color: config.value.instances?.instanceStyle.defaultColour,
      backgroundColor: config.value.instances?.instanceStyle.defaultBackground,
      displayIfZero: false,
      position: undefined,
      recordCount: undefined,
      type: undefined,
    }
  );
}

function stringToDate(dateString: string, conf?: { hours: number; minutes: number; seconds: number }) {
  if (stringToDateCache[dateString]) return new Date(stringToDateCache[dateString]);

  const { hours = 0, minutes = 0, seconds = 0 } = conf || {};
  const date = toMoment(dateString, true)!.toDate();
  date.setHours(hours, minutes, seconds);
  stringToDateCache[dateString] = date;
  return new Date(date);
}

function goToFile(recordCluster: Metadata, fileReference: string) {
  if (
    !filteredItems.value.length ||
    !filteredItems.value.includes(recordCluster.fileReference) ||
    lastFilteredFileReference.value === fileReference
  ) {
    changeDateFilter(recordCluster);
  }
  lastFilteredFileReference.value = fileReference;
  const { selectedCaseName } = useCaseStore();
  $router.push({
    name: "casefile",
    params: { brand: routeParams.value.brand, casename: selectedCaseName, casefile: fileReference },
  });
}
</script>
<template>
  <div class="tw-relative tw-h-16 *:tw-size-full">
    <h4 v-if="dateRange.length === 0" class="tw-grid tw-w-full tw-place-items-center">
      <p class="tw-w-1/2 tw-text-center">There are no dated records in this case</p>
    </h4>
    <div v-else class="tw-flex tw-divide-x tw-divide-black">
      <div ref="containerEl" class="tw-flex-grow">
        <svg
          ref="zoomRef"
          :class="`${timelineState === TimelineState.ZOOMED ? 'cursor-ew-resize' : ''}`"
          class="tw-size-full"
        >
          <g
            v-if="config.general?.useLower"
            class="axes-horizontal"
            :transform="`translate(${DIMENSIONS_PADDING_LEFT}, ${dimensions.height + DIMENSIONS_PADDING_TOP})`"
          >
            <g class="line">
              <line :x2="dimensions.width" :stroke="AXIS_STROKE_COLOR" />
            </g>
            <g class="tick">
              <line
                v-for="tick in ticks"
                :key="String(tick)"
                :y1="0"
                :y2="AXIS_BAND_HEIGHT"
                :transform="`translate(${xScale(tick)! + EDGE_ALIGNMENT_OFFSET_X}, 0)`"
                :stroke="AXIS_STROKE_COLOR"
              />
            </g>
            <g class="line">
              <line
                :x2="dimensions.width"
                :transform="`translate(0, ${AXIS_BAND_HEIGHT})`"
                :stroke="AXIS_STROKE_COLOR"
              />
            </g>
            <g class="tick-content">
              <text
                v-for="tick in ticks"
                :key="String(tick)"
                :transform="`translate(${xScale(tick)! + AXIS_LABEL_OFFSET_X}, ${AXIS_LABEL_OFFSET_Y})`"
              >
                {{ xScale.tickFormat(numberOfTicks)(tick) }}
              </text>
            </g>
            <g class="move-icons">
              <g
                v-if="timelineState === TimelineState.ZOOMED || timelineState === TimelineState.ZOOMED_LEFT_END"
                :width="MOVE_ICON_WIDTH"
                :height="MOVE_ICON_HEIGHT"
                :transform="`translate(${dimensions.width - MOVE_ICON_WIDTH - MOVE_ICON_OFFSET_X}, ${MOVE_ICON_OFFSET_Y})`"
                class="right-icon"
                :fill-opacity="MOVE_ICON_OPACITY"
                @click="pan($event, -PAN_STEP)"
              >
                <path
                  d="M18.59 13H3a1 1 0 0 1 0-2h15.59l-5.3-5.3a1 1 0 1 1 1.42-1.4l7 7a1 1 0 0 1 0 1.4l-7 7a1 1 0 0 1-1.42-1.4l5.3-5.3z"
                />
              </g>
              <g
                v-if="timelineState === TimelineState.ZOOMED || timelineState === TimelineState.ZOOMED_RIGHT_END"
                :width="MOVE_ICON_WIDTH"
                :height="MOVE_ICON_HEIGHT"
                :transform="`translate(${MOVE_ICON_OFFSET_X}, ${MOVE_ICON_OFFSET_Y})`"
                class="right-icon"
                :fill-opacity="MOVE_ICON_OPACITY"
                @click="pan($event, PAN_STEP)"
              >
                <path
                  d="M5.41 11H21a1 1 0 0 1 0 2H5.41l5.3 5.3a1 1 0 0 1-1.42 1.4l-7-7a1 1 0 0 1 0-1.4l7-7a1 1 0 0 1 1.42 1.4L5.4 11z"
                />
              </g>
            </g>
          </g>
          <g class="bound" :transform="`translate(${DIMENSIONS_PADDING_LEFT}, ${DIMENSIONS_PADDING_TOP})`">
            <g v-if="config.general?.useUpper" class="agreement-periods">
              <g
                v-for="period in agreementPeriods"
                :key="period.from + period['Service Type'] + period['Service Obligation']"
              >
                <rect
                  :height="AXIS_BAND_HEIGHT"
                  :width="xScale(stringToDate(period.to, END_OF_DAY))! - xScale(stringToDate(period.from))!"
                  :fill-opacity="0.5"
                  :transform="`translate(${xScale(stringToDate(period.from))! + EDGE_ALIGNMENT_OFFSET_X}, 0)`"
                  stroke="black"
                  :fill="AGREEMENT_PERIOD_FILL"
                />
                <text
                  :transform="`translate(${xScale(stringToDate(period.from))! + AXIS_LABEL_OFFSET_X}, ${AXIS_LABEL_OFFSET_Y})`"
                >
                  {{ period.from }}
                </text>
                <polygon
                  :points="`${xScale(stringToDate(period.to, END_OF_DAY))},${AXIS_BAND_HEIGHT} ${
                    xScale(stringToDate(period.to, END_OF_DAY))! - GRACE_TRIANGLE_WIDTH
                  },${AXIS_BAND_HEIGHT} ${xScale(stringToDate(period.to, END_OF_DAY))},0`"
                  style="fill: white; stroke: purple; stroke-width: 1"
                />
                <text
                  :transform="`translate(${xScale(stringToDate(period.to))! + GRACE_LABEL_OFFSET_X}, ${GRACE_LABEL_OFFSET_Y})`"
                  font-size="10px"
                >
                  <template v-for="periodOption in periodOptions">
                    {{ agreementPeriodConfig[periodOption.name][period[periodOption.name] ?? ""] }}
                  </template>
                </text>
                <rect
                  v-if="!isGraceDisabled"
                  :height="AXIS_BAND_HEIGHT"
                  :width="
                    xScale(stringToDate(period.graceTo, END_OF_DAY))! - xScale(stringToDate(period.to, END_OF_DAY))!
                  "
                  :transform="`translate(${xScale(stringToDate(period.to, END_OF_DAY))}, 0)`"
                  shape-rendering="crispEdges"
                  :fill="AGREEMENT_PERIOD_FILL"
                />
              </g>
            </g>
            <template v-if="config.lower?.showRecordClusters">
              <g
                v-for="d in recordClusters"
                :key="d.fileReference"
                :transform="`translate(0, ${dimensions.height})`"
                class="record-clusters"
                @click="changeDateFilter(d)"
              >
                <circle
                  :cx="d.x"
                  :r="d.r"
                  :stroke="filteredItems.includes(d.fileReference) ? 'black' : 'lightgrey'"
                  fill="yellow"
                />
                <g
                  v-for="(iconConfig, index) in d.iconConfigs"
                  :key="index"
                  :transform="`translate(${d.x - d.r + index * RECORD_CLUSTER_ICON_SPACING_X}, ${RECORD_CLUSTER_ICON_OFFSET_Y})`"
                  @click.stop="goToFile(d, iconConfig.original)"
                >
                  <svg
                    :fill="iconConfig.config.color"
                    :width="RECORD_CLUSTER_ICON_WIDTH"
                    :height="RECORD_CLUSTER_ICON_HEIGHT"
                    viewBox="0 0 20 20"
                    v-html="iconConfig.config.icon"
                  />
                  <!-- make sure to render after other content for stacking order. size & position props copied from above svg -->
                  <foreignObject :width="RECORD_CLUSTER_ICON_WIDTH" :height="RECORD_CLUSTER_ICON_HEIGHT">
                    <TagTooltip v-if="iconConfig.type === 'tag'" side="bottom" :item="[iconConfig.value]">
                      <button type="button" class="tw-size-full" />
                    </TagTooltip>
                    <EvidenceCountTooltip
                      v-else-if="iconConfig.type === 'evidenceCount'"
                      side="bottom"
                      :item="iconConfig.value"
                    >
                      <button type="button" class="tw-size-full" />
                    </EvidenceCountTooltip>
                  </foreignObject>
                </g>
              </g>
            </template>
            <template v-if="config.general?.useInstances">
              <template v-for="(ioa, index) in ioas.list" :key="ioa.instanceReference">
                <g
                  class="instance-advices"
                  :transform="`translate(0, ${
                    ioAsAssociated.has(ioa.instanceReference)
                      ? INSTANCE_ROW_OFFSET_Y_ASSOCIATED
                      : INSTANCE_ROW_OFFSET_Y_UNASSOCIATED
                  })`"
                  @click="applyInstanceFilter(ioa)"
                >
                  <g :height="INSTANCE_LABEL_HEIGHT" :width="ioa.width">
                    <rect
                      :height="INSTANCE_LABEL_HEIGHT"
                      :width="ioa.width"
                      :x="ioa.x"
                      :stroke="ioa.instanceReference === instanceFilter ? 'black' : 'white'"
                      :fill="instanceBg(ioa)"
                      :rx="
                        ioAsAssociated.has(ioa.instanceReference)
                          ? INSTANCE_CORNER_RADIUS_ASSOCIATED
                          : INSTANCE_CORNER_RADIUS_UNASSOCIATED
                      "
                      :ry="
                        ioAsAssociated.has(ioa.instanceReference)
                          ? INSTANCE_CORNER_RADIUS_ASSOCIATED
                          : INSTANCE_CORNER_RADIUS_UNASSOCIATED
                      "
                    />
                    <text
                      v-if="config.instances?.showSequenceNumber"
                      :x="ioa.x + ioa.width / 2"
                      :stroke="instanceColor(ioa)"
                      dy="1em"
                      text-anchor="middle"
                    >
                      {{ index + 1 }}
                    </text>
                    <!-- make sure to render after other content for stacking order. size & position props copied from above rect -->
                    <foreignObject :height="INSTANCE_LABEL_HEIGHT" :width="ioa.width" :x="ioa.x">
                      <TagTooltip side="bottom" :item="ioa.tags">
                        <button type="button" class="tw-size-full" />
                      </TagTooltip>
                    </foreignObject>
                  </g>
                  <template v-for="(count, evidenceCountIdx) in ioa.evidenceCounts" :key="evidenceCountIdx">
                    <template
                      v-for="(countConfig, countConfigIdx) in [evidenceCountConfig(count.name)]"
                      :key="countConfigIdx"
                    >
                      <g
                        v-if="countConfig.displayIfZero || count.value"
                        :transform="`translate(${ioa.x + evidenceCountIdx * EVIDENCE_COUNT_BADGE_SPACING_X}, ${
                          countConfig.position === 'lower'
                            ? EVIDENCE_COUNT_BADGE_OFFSET_Y_LOWER
                            : EVIDENCE_COUNT_BADGE_OFFSET_Y_UPPER
                        })`"
                      >
                        <g>
                          <rect
                            :fill="countConfig.backgroundColor"
                            :height="EVIDENCE_COUNT_BADGE_HEIGHT"
                            :width="EVIDENCE_COUNT_BADGE_WIDTH"
                            :rx="EVIDENCE_COUNT_BADGE_CORNER_RADIUS"
                            :ry="EVIDENCE_COUNT_BADGE_CORNER_RADIUS"
                          />
                          <text :stroke="countConfig.color" :dy="9" :dx="8" font-size="8">
                            {{ count.value }}
                          </text>
                          <!-- make sure to render after other content for stacking order. size & position props copied from above rect -->
                          <foreignObject :height="EVIDENCE_COUNT_BADGE_HEIGHT" :width="EVIDENCE_COUNT_BADGE_WIDTH">
                            <EvidenceCountTooltip side="bottom" :item="{ name: count.name, value: count.value }">
                              <button type="button" class="tw-size-full" />
                            </EvidenceCountTooltip>
                          </foreignObject>
                        </g>
                      </g>
                    </template>
                  </template>
                </g>
              </template>
            </template>
          </g>
        </svg>
      </div>
      <div
        class="tw-grid tw-grid-flow-row tw-auto-rows-fr tw-gap-1 tw-bg-gray-900 tw-p-1 tw-text-white *:tw-cursor-pointer"
      >
        <ConfirmPopover v-if="config.general?.useUpper && config.upper">
          <template #trigger>
            <i class="far fa-calendar-alt" />
          </template>
          <template #question>
            <label class="tw-mb-1">{{
              agreementPeriods.length === 1 ? config.upper.periodNameSingular : config.upper.periodNamePlural
            }}</label>
            <div class="tw-relative">
              <select
                class="tw-hover:border-gray-500 tw-focus:outline-none tw-focus:shadow-outline tw-block tw-w-full tw-appearance-none tw-rounded-sm tw-border tw-border-gray-400 tw-bg-white tw-px-3 tw-py-1 tw-pr-8 tw-leading-tight tw-shadow"
                :value="selectedPeriod.id"
                @change="(ev) => (periodId = (ev.target as HTMLSelectElement).value)"
              >
                <option v-for="period in agreementPeriods" :key="period.id" :value="period.id">
                  {{ period.from }} - {{ period.to }}
                  {{ !isGraceDisabled && period.graceMonths ? `[${period.graceTo}]` : "" }}
                </option>
              </select>
              <div
                class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-px-2 tw-text-gray-700"
              >
                <svg class="tw-h-4 tw-w-4 tw-fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
            <template v-for="periodOption in periodOptions" :key="periodOption.name">
              <div class="tw-mt-3">
                <label class="tw-mb-1">{{ periodOption.name }}</label>
                <div v-if="periodOption.type === 'manual'" class="tw-relative tw-mb-3">
                  <select
                    v-model="selectedPeriod[periodOption.name]"
                    :disabled="!$can('annotate')"
                    class="tw-hover:border-gray-500 tw-focus:outline-none tw-focus:shadow-outline tw-block tw-w-full tw-appearance-none tw-rounded-sm tw-border tw-border-gray-400 tw-bg-white tw-px-3 tw-py-1 tw-pr-8 tw-leading-tight tw-shadow"
                    @change="onPeriodChanged"
                  >
                    <option v-for="item in periodOption.enumeration" :key="item.name" :value="item.name">
                      {{ item.name }}
                    </option>
                  </select>
                  <div
                    class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-px-2 tw-text-gray-700"
                  >
                    <svg class="tw-h-4 tw-w-4 tw-fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                <template v-if="periodOption.type === 'ioas'">
                  <div v-if="ioAsToAssociate.length" class="tw-relative tw-mb-1">
                    <select
                      :disabled="!$can('annotate')"
                      class="tw-hover:border-gray-500 tw-focus:outline-none tw-focus:shadow-outline tw-block tw-w-full tw-appearance-none tw-rounded-sm tw-border tw-border-gray-400 tw-bg-white tw-px-3 tw-py-1 tw-pr-8 tw-leading-tight tw-shadow"
                      @change="addIoaToPeriod($event)"
                    >
                      <option value="">Select {{ periodOption.name }}</option>
                      <option
                        v-for="item in ioAsToAssociate"
                        :key="item.instanceReference"
                        :value="item.instanceReference"
                      >
                        {{ item.index + 1 }}
                      </option>
                    </select>
                    <div
                      class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-px-2 tw-text-gray-700"
                    >
                      <svg class="tw-h-4 tw-w-4 tw-fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                  <div v-if="selectedPeriod.ioas && selectedPeriod.ioas.length">
                    <template v-for="ioa in selectedPeriod.ioas" :key="ioa">
                      <button
                        v-if="ioas.map.has(ioa)"
                        :disabled="!$can('annotate')"
                        class="tw-m-1 hover:tw-opacity-75"
                        title="click to remove"
                        @click.prevent="deleteIoaFromPeriod(ioa)"
                      >
                        [<strong>#{{ ioas.map.get(ioa)!.index + 1 }}</strong
                        >]
                      </button>
                    </template>
                  </div>
                </template>
              </div>
            </template>
          </template>
        </ConfirmPopover>
        <StringTooltip #="{ item }" item="Zoom In" side="left">
          <button type="button" :aria-label="item" @click="scale(2)">
            <i class="fa fa-search-plus" />
          </button>
        </StringTooltip>
        <StringTooltip #="{ item }" item="Zoom Out" side="left">
          <button type="button" :aria-label="item" @click="scale(0.5)">
            <i class="fa fa-search-minus" />
          </button>
        </StringTooltip>
      </div>
    </div>
  </div>
</template>
<style type="text/css" scoped>
.record-clusters,
.instance-advices,
.move-icons > * {
  cursor: pointer;
}
svg:not(:root) {
  overflow: hidden !important;
}
.cursor-ew-resize {
  cursor: ew-resize;
}
i {
  font-size: 14px;
}
</style>
